import { v4 } from "uuid";

import type { CuratedPlaceOfSafety } from "@thalamos/common";
import { IncidentWorkItemStatusSchema } from "@thalamos/common";
import {
  FormSectionSchema,
  IncidentFormIdSchema,
  IncidentFormTypeSchema,
  type ExtendedVisionUser,
  type IncidentWorkItemId,
} from "@thalamos/vision-common";

import api from "../api/index.js";
import {
  createTriagedIncident,
  pickRandomPlaceOfSafety,
  quickCreateContextOf,
} from "./quick_create.js";
import {
  getQuickCreateSampleData,
  type PersonName,
} from "./quickCreateSampleData.js";

/**
 * Creates a 136 incident populated up to and including Place of Safety arrival,
 * but stopping before sharing with NHS, so testing the later steps does not
 * require completing a dozen form pages by hand.
 *
 * Anything the caller does not supply is fabricated: omit `name` or
 * `curatedPlaceOfSafety` and a random one is used.
 *
 * A testing aid: callers must gate on `isQuickCreateEnabled`. Throws on the
 * first failed step, leaving a part-built incident behind, so the caller can
 * report what went wrong.
 */
export async function runQuickCreate136({
  user,
  name,
  curatedPlaceOfSafety,
}: {
  user: ExtendedVisionUser;
  name?: PersonName;
  curatedPlaceOfSafety?: CuratedPlaceOfSafety;
}): Promise<IncidentWorkItemId> {
  const context = quickCreateContextOf(user);
  const { sessionContext } = context;

  const sampleData = getQuickCreateSampleData(context.workflowVersion, {
    name,
    curatedPlaceOfSafety:
      curatedPlaceOfSafety ?? (await pickRandomPlaceOfSafety()),
  });

  const { incidentWorkItemId, formId: section136FormId } =
    await createTriagedIncident({
      context,
      sampleData,
      formType: IncidentFormTypeSchema.enum.section136,
    });

  await api.incidentWorkItem.updateData(
    incidentWorkItemId,
    section136FormId,
    FormSectionSchema.enum.personDetails,
    sampleData.personDetails,
    true,
  );

  // In BTP (and other workflows), orgResponsibleForDetention is pre-populated
  // from the create-incident commandUnit and can't be deselected. Mirror that
  // by overriding it to match the session organisation.
  await api.incidentWorkItem.updateData(
    incidentWorkItemId,
    section136FormId,
    FormSectionSchema.enum.detentionDetails,
    {
      ...sampleData.detentionDetails,
      orgResponsibleForDetention: {
        value: sessionContext.organisationId,
        valueLabel: sessionContext.organisationName,
      },
    },
    true,
  );

  await api.incidentWorkItem.updateData(
    incidentWorkItemId,
    section136FormId,
    FormSectionSchema.enum.informationForHealthcare,
    sampleData.informationForHealthcare,
    true,
  );

  const placeOfSafetyFormId = IncidentFormIdSchema.parse(v4());
  await api.incidentWorkItem.createPlaceOfSafetyForm(
    incidentWorkItemId,
    placeOfSafetyFormId,
  );

  await api.incidentWorkItem.updateData(
    incidentWorkItemId,
    placeOfSafetyFormId,
    FormSectionSchema.enum.recordPlaceOfSafetyMain,
    sampleData.recordPlaceOfSafetyMain,
    true,
  );

  await api.incidentWorkItem.updateData(
    incidentWorkItemId,
    placeOfSafetyFormId,
    FormSectionSchema.enum.recordPlaceOfSafetyArrival,
    sampleData.recordPlaceOfSafetyArrival,
    true,
    IncidentWorkItemStatusSchema.enum["arrived-at-place-of-safety"],
  );

  return incidentWorkItemId;
}
